import { createContext, useContext } from 'react';

// Define the shape of the context data

// Set the data types for the menu data that we get from the backend
export interface ComboSection {
  section_name_code: string;
  united_sections: string[];
  incompatible_elements: string[];
  display_order: number;
  is_active: boolean;
  is_multiple_choices: boolean;
  section_elements: string[];
}

// This represents the entire 'combo' array
export type Combo = ComboSection[];

export type AvailableTimeSlot = [string, string];
export type AvailableDayValue = AvailableTimeSlot[] | [] | null;
export type AvailableDatesYear = Record<string, Record<string, AvailableDayValue>>;
export type ProductDeliveryConditions = {
  preparation_time?: number | null;
  nearest_delivery_interval?: number | null;
};
export type AvailableDates = Record<string, AvailableDatesYear> | null;

/**
 * Returns true only when the payload contains calendar year/week/day rules.
 * Only numeric year/week/day rules participate in product availability. Other
 * keys are ignored defensively so malformed backend data cannot activate the
 * custom calendar editor.
 */
export const hasCustomAvailabilitySchedule = (value: unknown): boolean => {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return false;
  }

  return Object.entries(value as Record<string, unknown>).some(([yearKey, yearValue]) => {
    if (
      !/^\d+$/.test(yearKey)
      || yearValue == null
      || typeof yearValue !== 'object'
      || Array.isArray(yearValue)
    ) {
      return false;
    }

    return Object.entries(yearValue as Record<string, unknown>).some(([weekKey, weekValue]) => (
      /^\d+$/.test(weekKey)
      && weekValue != null
      && typeof weekValue === 'object'
      && !Array.isArray(weekValue)
      && Object.keys(weekValue as Record<string, unknown>).some((dayKey) => /^\d+$/.test(dayKey))
    ));
  });
};

/**
 * Reads the dedicated product delivery conditions without allowing malformed
 * backend values to affect scheduling, editor rendering, or card summaries.
 */
export const getProductDeliveryConditions = (value: unknown): ProductDeliveryConditions | null => {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return null;
  }

  const conditions = value as Partial<ProductDeliveryConditions>;
  const preparationTime = Number.isInteger(conditions.preparation_time) && Number(conditions.preparation_time) >= 0
    ? Number(conditions.preparation_time)
    : null;
  const nearestInterval = Number.isInteger(conditions.nearest_delivery_interval) && Number(conditions.nearest_delivery_interval) >= 1
    ? Number(conditions.nearest_delivery_interval)
    : null;

  return preparationTime !== null || nearestInterval !== null
    ? { preparation_time: preparationTime, nearest_delivery_interval: nearestInterval }
    : null;
};
export type PriceLevelPriceMap = Record<string, number | string | Record<string, number | string> | null>;

export interface MenuItem {
  name_code: string;
  is_available: boolean;
  is_deleted?: boolean;
  price?: number | string | null;
  version_price: { [key: string]: number } | null;
  version_prices_by_levels?: PriceLevelPriceMap | null;
  is_active: boolean;
  master_product_name_code: string | null;
  available_dates: AvailableDates;
  delivery_conditions?: ProductDeliveryConditions | null;
  image: string;
  is_delivered: boolean;
  description_code: string | null;
  display_order: number;
  is_add: boolean;
  possible_adds: string[] | null;
  category_code: string;
  combo: Combo | null;
  product_versions: string[] | null;
  food_maker_name_code: string;
  no_version_price: number | null;
  min_quantity: number | null;
  quantity?: number | null;
  remaining_stock?: number | string | null;
  unit_code: string | null;
  unit_step: number | null;
  weight_name_code?: string | null;
  volume_name_code?: string | null;
}

export interface MenuTranslations {
  [key: string]: { // Index signature for dynamic keys
    [lang: string]: string;
  };
}

export interface FoodMakerMenu {
  [key: string]: MenuItem;
}

export interface MenuDataType {
  food_maker_menu: FoodMakerMenu;
  menu_lang_texts: MenuTranslations;      // The texts for the menu in different languages
  master_lang_codes: string[];            // The master products language codes in case if the menu positions inherit the texts from the master product
}



// The context may be null before data is loaded

const PartnerFullMenuDataContext = createContext<MenuDataType | null>(null);

export const usePartnerFullMenuData = () => useContext(PartnerFullMenuDataContext);

export const AdminMenuRefreshContext = createContext<(() => Promise<void>) | null>(null);
export const useAdminMenuRefresh = () => useContext(AdminMenuRefreshContext);

export default PartnerFullMenuDataContext;
